import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { TEMPORAL_NAMESPACE, temporalWorkflowUrl } from '~/constants/config/env';
import { useAuthStore } from '~/store/auth.store';

import { useWorkflowDescribe } from '../hooks/use-workflow-inspection';
import { isHistoryExpired } from '../utils/inspection-errors';

interface Props {
  workflowId: string | null | undefined;
  /** Temporal namespace — delivery workflows run in `agentic-integrations` (the default). */
  namespace?: string;
}

/**
 * Opens the same workflow in the Temporal Web UI for the current environment
 * (new tab). Super-admin only, like the app's other Temporal deep links — the
 * Temporal UI is an internal tool tenant users can't sign into. Renders nothing
 * without a workflow id. Below 1440px it collapses to an icon button with the
 * label in a tooltip (the tooltip is hidden while the label is visible).
 *
 * Reads the same describe query as `WorkflowInspector` (shared cache entry, so no
 * extra call where the inspector is mounted): once it 404s — the execution aged out
 * of Temporal retention — the Temporal UI would 404 too, so the button renders
 * disabled with a tooltip saying why instead of linking there.
 */
export function OpenInTemporalButton({
  workflowId,
  namespace = TEMPORAL_NAMESPACE.integrations,
}: Props) {
  const { t } = useTranslation('workflow-inspection');
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false);
  const href = temporalWorkflowUrl(workflowId, namespace);
  const describe = useWorkflowDescribe(workflowId ?? '', null, isSuperAdmin && !!href);
  if (!isSuperAdmin || !href) return null;

  if (isHistoryExpired(describe.error)) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Disabled buttons swallow pointer events; the span keeps the tooltip reachable. */}
          <span className="inline-flex cursor-not-allowed">
            <Button
              data-testid="workflow-inspector-open-in-temporal-expired-button"
              variant="outline"
              disabled
              aria-label={`${t('temporal.open')} — ${t('temporal.expired')}`}
              className="pointer-events-none cursor-not-allowed gap-1.5"
            >
              <ExternalLink aria-hidden="true" className="size-4" />
              <span className="hidden min-[1440px]:inline">{t('temporal.open')}</span>
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{t('temporal.expired')}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="outline" asChild className="cursor-pointer gap-1.5">
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('temporal.open')}
            data-testid="workflow-inspector-open-in-temporal-link"
          >
            <ExternalLink aria-hidden="true" className="size-4" />
            <span className="hidden min-[1440px]:inline">{t('temporal.open')}</span>
          </a>
        </Button>
      </TooltipTrigger>
      <TooltipContent className="min-[1440px]:hidden">{t('temporal.open')}</TooltipContent>
    </Tooltip>
  );
}
